import { useEffect, useState, useCallback, useMemo } from "react";
import { ActionsButton } from "@/components/ActionsButton";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, FileText, Pencil, Copy, Send, Trash2, Printer, Download, Truck } from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DdtBuilder from "@/components/ddt/DdtBuilder";
import DdtPDF from "@/components/ddt/DdtPDF";
import SendDialog from "@/components/SendDialog";
import { downloadElementPdf } from "@/lib/pdf";
import { useCompanyGate } from "@/components/company/useCompanyGate";

const fileName = (d) => `DDT-${String(d?.numero || "").replace(/\//g, "-")}.pdf`;

export default function Ddt() {
  const [list, setList] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [anno, setAnno] = useState("tutti");
  const [builder, setBuilder] = useState(null);
  const [view, setView] = useState(null);
  const [sendDoc, setSendDoc] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [params, setParams] = useSearchParams();
  const { ready: profileReady, ensure, gate } = useCompanyGate();

  const load = useCallback(() => {
    Promise.all([cachedGet("/ddt"), cachedGet("/contacts"), cachedGet("/quotes"), cachedGet("/settings")])
      .then(([d, c, qs, s]) => { setList(d.data); setContacts(c.data); setQuotes(qs.data); setSettings(s.data); });
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const fq = params.get("from_quote");
    if (fq && profileReady) { ensure(() => setBuilder({ initial: null, importQuoteId: fq })); setParams({}, { replace: true }); }
  }, [params, setParams, profileReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const years = useMemo(() => [...new Set(list.map((d) => d.anno))].sort((a, b) => b - a), [list]);
  const filtered = list.filter((d) => (anno === "tutti" || String(d.anno) === anno)
    && (!q || [d.numero, d.destinatario_nome, d.causale, d.quote_numero].some((v) => String(v || "").toLowerCase().includes(q.toLowerCase()))));
  const contactOf = (d) => contacts.find((c) => c.id === d?.contact_id);

  const onSaved = () => { setBuilder(null); load(); };
  const duplicate = (d) => ensure(() => setBuilder({ initial: { ...d, id: undefined, numero: undefined, progressivo: "", data: new Date().toISOString() } }));
  const doDelete = async () => {
    await api.delete(`/ddt/${toDelete.id}`);
    toast.success(`DDT N. ${toDelete.numero} eliminato`);
    setToDelete(null); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap w-full sm:w-auto">
          <div className="relative flex-1 sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Cerca N., destinatario, causale…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="ddt-search-input" />
          </div>
          <Select value={anno} onValueChange={setAnno}>
            <SelectTrigger className="w-32" data-testid="ddt-year-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti gli anni</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => ensure(() => setBuilder({ initial: null }))} data-testid="new-ddt-button"><Plus className="w-4 h-4 mr-1.5" /> Nuovo DDT</Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <Table data-testid="ddt-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>N.</TableHead>
              <TableHead>Destinatario</TableHead>
              <TableHead className="hidden md:table-cell">Data</TableHead>
              <TableHead className="hidden md:table-cell">Causale</TableHead>
              <TableHead className="hidden lg:table-cell">Rif. Prev.</TableHead>
              <TableHead className="hidden sm:table-cell text-right">Righe</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((d) => (
              <TableRow key={d.id} className="cursor-pointer" onClick={() => setView(d)} data-testid={`ddt-row-${d.numero}`}>
                <TableCell className="font-mono text-xs font-medium whitespace-nowrap">
                  {d.numero}
                  {d.tipo === "reso" && <Badge variant="outline" className="ml-2 text-[10px] bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300">Reso</Badge>}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{d.destinatario_nome}</div>
                  {d.luogo_destinazione && <div className="text-xs text-muted-foreground truncate max-w-[240px]">{d.luogo_destinazione}</div>}
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{fmtDate(d.data)}</TableCell>
                <TableCell className="hidden md:table-cell text-sm">{d.causale}</TableCell>
                <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{d.quote_numero || "—"}</TableCell>
                <TableCell className="hidden sm:table-cell text-right text-sm">{d.righe?.length || 0}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <ActionsButton data-testid={`ddt-actions-${d.numero}`} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setView(d)}><FileText className="w-4 h-4 mr-2" /> Anteprima / PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setBuilder({ initial: d })} data-testid={`ddt-edit-${d.numero}`}><Pencil className="w-4 h-4 mr-2" /> Modifica</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => duplicate(d)} data-testid={`ddt-duplicate-${d.numero}`}><Copy className="w-4 h-4 mr-2" /> Duplica</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSendDoc(d)} data-testid={`ddt-send-${d.numero}`}><Send className="w-4 h-4 mr-2" /> Invia (WhatsApp/Email)</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => setToDelete(d)} data-testid={`ddt-delete-${d.numero}`}><Trash2 className="w-4 h-4 mr-2" /> Elimina</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-14 text-muted-foreground">
                <Truck className="w-8 h-8 mx-auto mb-2 opacity-40" />Nessun DDT. Creane uno nuovo o importalo da un preventivo.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <DdtBuilder open={!!builder} onClose={() => setBuilder(null)} onSaved={onSaved} initial={builder?.initial} importQuoteId={builder?.importQuoteId} contacts={contacts} quotes={quotes} />
      {gate}

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="ddt-preview-modal">
          <DialogTitle className="sr-only">Anteprima DDT N. {view?.numero}</DialogTitle>
          <DialogDescription className="sr-only">Anteprima, stampa e download del documento di trasporto.</DialogDescription>
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 flex-wrap px-5 py-3 border-b border-border bg-card no-print">
            <div className="font-heading font-semibold">DDT N. {view?.numero}</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1.5" /> Stampa</Button>
              <Button size="sm" variant="outline" onClick={() => { setSendDoc(view); setView(null); }} data-testid="ddt-preview-send-button"><Send className="w-4 h-4 mr-1.5" /> Invia</Button>
              <Button size="sm" onClick={() => downloadElementPdf("print-area", fileName(view))} data-testid="ddt-pdf-download-button"><Download className="w-4 h-4 mr-1.5" /> Scarica PDF</Button>
            </div>
          </div>
          <div className="p-4 bg-slate-100 dark:bg-slate-900">
            <DdtPDF ddt={view} settings={settings} domId="print-area" />
          </div>
        </DialogContent>
      </Dialog>

      <SendDialog open={!!sendDoc} onClose={() => setSendDoc(null)} doc={sendDoc} contact={contactOf(sendDoc)} settings={settings} docType="ddt" />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il DDT?</AlertDialogTitle>
            <AlertDialogDescription>Il DDT N. {toDelete?.numero} sarà eliminato definitivamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-ddt">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
